import { Injectable } from '@angular/core';

import { Observable, map } from 'rxjs';

import { HttpApiService } from '@recruitee/http';
import { Location, LocationsScope, PaginationMeta } from '@recruitee/http-types';
import { ID } from '@recruitee/ngrx';
import { getOptions } from '@recruitee/walkthrough-utils';

import {
  LocationCreatePayload,
  LocationResponse,
  LocationsBriefResponse,
  LocationsPayloadParams,
  LocationsResponse,
} from '../location.types';

@Injectable({ providedIn: 'root' })
export class LocationsHttpClient {
  constructor(protected httpApi: HttpApiService) {}

  public fetchBriefCollection(): Observable<LocationsBriefResponse> {
    return this.httpApi.get('locations');
  }

  public fetchPaginatedCollection(
    scope: LocationsScope,
    params: LocationsPayloadParams,
  ): Observable<{ rows: Location[]; meta: PaginationMeta }> {
    return this.httpApi
      .get('locations', { scope, ...params, viewMode: 'full' })
      .pipe(map(data => ({ meta: data.meta, rows: data.locations })));
  }

  public fetchWithMissingTranslations(langCode: string): Observable<Location[]> {
    return this.httpApi
      .get('locations', { viewMode: 'full', withMissingTranslation: langCode })
      .pipe(map(data => data.locations));
  }

  public fetchOne(id: ID): Observable<LocationResponse> {
    return this.httpApi.get(`/locations/${id}`);
  }

  public create(body: LocationCreatePayload): Observable<LocationResponse> {
    return this.httpApi.post('/locations', {}, body);
  }

  public update(body: LocationCreatePayload, id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}`, {}, body);
  }

  public archive(id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}/archive`);
  }

  public retrieve(id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}/retrieve`);
  }

  public duplicate(id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}/duplicate`);
  }

  public delete(id: ID): Observable<LocationResponse> {
    return this.httpApi.delete(`/locations/${id}`);
  }

  public fetchAssignedLocations(offerId: ID): Observable<LocationsResponse> {
    return this.httpApi.get(
      `offers/${offerId}/locations`,
      null,
      getOptions({ hasOnboarding: true }),
    );
  }
}
